import { fetchAutocropBbox } from '../../../services/appImagesApi';

function getNaturalDimensions(htmlImage) {
  if (!htmlImage) return { width: 0, height: 0 };
  return {
    width: htmlImage.naturalWidth || htmlImage.width || 0,
    height: htmlImage.naturalHeight || htmlImage.height || 0,
  };
}

function clearKonvaCrop(imageNode) {
  imageNode.setAttrs({
    crop: undefined,
    cropX: undefined,
    cropY: undefined,
    cropWidth: undefined,
    cropHeight: undefined,
  });
}

function setBgPattern(bgRect, imageSource) {
  if (!bgRect || !imageSource) return;
  bgRect.fillPatternImage(imageSource);
  bgRect.fillPatternX(0);
  bgRect.fillPatternY(0);
}

/**
 * Apply backend bbox the same way the old client cropImage did: extract pixels
 * into a canvas so initWidth/initHeight match the visible image (required for
 * scale mode fillPattern, which uses the full image source).
 */
function cropHtmlImageByBbox(htmlImage, bbox) {
  const { x, y, width, height } = bbox;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(htmlImage, x, y, width, height, 0, 0, width, height);
  return canvas;
}

export function loadImageForAutocrop(url) {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.onload = () => resolve(img);
    img.onerror = () =>
      reject(new Error(`Failed to load image: ${String(url).slice(0, 100)}`));
    img.src = url;
  });
}

async function resolveFullSourceImage(imageNode) {
  const current = imageNode.image();
  const url = imageNode.getAttr('url');
  if (url) {
    try {
      return await loadImageForAutocrop(url);
    } catch (e) {
      if (current) return current;
      throw e;
    }
  }
  return current;
}

export async function applyAutocropToNode(imageNode, bgRect, response) {
  const hasCrop = Boolean(response && response.cropped && response.bbox);

  if (!hasCrop) {
    clearKonvaCrop(imageNode);
    const htmlImage = await resolveFullSourceImage(imageNode);
    if (!htmlImage) return;

    const natural = getNaturalDimensions(htmlImage);
    imageNode.image(htmlImage);
    imageNode.setAttr('initWidth', natural.width);
    imageNode.setAttr('initHeight', natural.height);
    setBgPattern(bgRect, htmlImage);
    return;
  }

  const sourceImage = await resolveFullSourceImage(imageNode);
  if (!sourceImage) return;

  const { x, y, width, height } = response.bbox;
  let croppedSource;
  try {
    croppedSource = cropHtmlImageByBbox(sourceImage, { x, y, width, height });
  } catch (e) {
    console.warn('Autocrop: canvas extract failed, using Konva crop attrs', e);
    imageNode.setAttrs({
      crop: { x, y, width, height },
      cropX: x,
      cropY: y,
      cropWidth: width,
      cropHeight: height,
    });
    imageNode.setAttr('initWidth', width);
    imageNode.setAttr('initHeight', height);
    setBgPattern(bgRect, sourceImage);
    return;
  }

  clearKonvaCrop(imageNode);
  imageNode.image(croppedSource);
  imageNode.setAttr('initWidth', width);
  imageNode.setAttr('initHeight', height);
  setBgPattern(bgRect, croppedSource);
}

export async function fetchAndApplyAutocrop(imageNode, bgRect, url, useFirstPixel) {
  const response = await fetchAutocropBbox(url, useFirstPixel);
  await applyAutocropToNode(imageNode, bgRect, response);
  return response;
}
